import { Flex } from "@redotech/arbiter/flex";
import { RedoIcon } from "@redotech/arbiter/icon/redo-icon";
import { Text } from "@redotech/arbiter/text";
import DollarSignIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import LayoutDashboardIcon from "@redotech/redo-icons/dashboard.svg";
import RedoLogo from "@redotech/redo-icons/redo-logo-cutout.svg";
import SettingsIcon from "@redotech/redo-icons/settings.svg";
import ShieldIcon from "@redotech/redo-icons/shield-tick.svg";
import { memo, useState } from "react";
import { NavLink } from "react-router-dom";

import { useAuthData } from "../auth/auth-data-context";
import * as css from "./sidebar.module.css";

export const Sidebar = memo(function Sidebar() {
  const { referrer } = useAuthData();
  const [failedProfileImageUrl, setFailedProfileImageUrl] = useState<
    string | null
  >(null);

  const initials =
    referrer?.name
      .split(" ")
      .map((w) => w[0])
      .filter((c): c is string => !!c)
      .slice(0, 2)
      .join("")
      .toUpperCase() ?? "—";

  return (
    <Flex
      as="aside"
      bgColor="primary"
      borderColor="secondary"
      borderRightWidth="1px"
      borderStyle="solid"
      dir="column"
      height="screen"
      position="sticky"
      px="lg"
      py="2xl"
      top="0"
    >
      <Flex pb="2xl" pt="sm" px="md">
        <a
          className={css.brandLink}
          href="https://www.redo.com"
          rel="noreferrer"
          target="_blank"
        >
          <RedoLogo className={css.brandLogo} />
          <Text
            as="span"
            fontSize="sm"
            fontWeight="regular"
            m="none"
            textColor="tertiary"
          >
            Referrals
          </Text>
        </a>
      </Flex>

      <Flex as="nav" dir="column" gap="none">
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/dashboard"
        >
          <RedoIcon
            className={css.navIcon}
            Icon={LayoutDashboardIcon}
            size={14}
          />
          <Text as="span" fontSize="sm" fontWeight="medium" m="none">
            Dashboard
          </Text>
        </NavLink>
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/earnings"
        >
          <RedoIcon className={css.navIcon} Icon={DollarSignIcon} size={14} />
          <Text as="span" fontSize="sm" fontWeight="medium" m="none">
            Earnings
          </Text>
        </NavLink>

        <Text
          as="div"
          className={css.sectionLabel}
          fontSize="xs"
          fontWeight="medium"
          m="none"
          pb="sm"
          pt="lg"
          px="md"
          textColor="tertiary"
        >
          Products
        </Text>
        <NavLink
          className={({ isActive }) =>
            `${css.navItem} ${isActive ? css.navItemActive : ""}`
          }
          to="/products/chargebacks"
        >
          <RedoIcon className={css.navIcon} Icon={ShieldIcon} size={14} />
          <Text as="span" fontSize="sm" fontWeight="medium" m="none">
            Chargebacks
          </Text>
        </NavLink>
      </Flex>

      <Flex
        align="center"
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        gap="md"
        mt="auto"
        pt="lg"
      >
        {referrer?.profileImageUrl &&
        failedProfileImageUrl !== referrer.profileImageUrl ? (
          <img
            alt=""
            aria-hidden
            className={css.avatar}
            onError={() => setFailedProfileImageUrl(referrer.profileImageUrl)}
            src={referrer.profileImageUrl}
          />
        ) : (
          <Flex
            align="center"
            aria-hidden
            bgColor="tertiary"
            borderRadius="full"
            className={css.avatar}
            justify="center"
            shrink={0}
          >
            <Text as="span" fontSize="xs" fontWeight="semibold" m="none">
              {initials}
            </Text>
          </Flex>
        )}
        <Flex dir="column" gap="none" grow={1} overflow="hidden">
          <Text
            as="span"
            fontSize="sm"
            fontWeight="medium"
            m="none"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {referrer?.name ?? "—"}
          </Text>
          <Text
            as="span"
            fontSize="xs"
            m="none"
            overflow="hidden"
            textColor="tertiary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {referrer?.email ?? ""}
          </Text>
        </Flex>
        <NavLink
          aria-label="Settings"
          className={({ isActive }) =>
            `${css.gear} ${isActive ? css.gearActive : ""}`
          }
          title="Settings"
          to="/settings"
        >
          <RedoIcon Icon={SettingsIcon} size={14} />
        </NavLink>
      </Flex>
    </Flex>
  );
});
